import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { EventEmitter, ElementRef, Component, Inject, Input, Output, HostListener, NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { mixinFormControl, FadvBaseComponent, createFormControlProvider, Forward, Track, formControlValueInput, formControlDisabledInput, formControlValueOutput, FadvCommonModule } from '@fa-switch-sharedservices/ng-elements/common';
import { checkLuhn } from '@fa-switch-sharedservices/web-elements/components/credit-card/credit-card';

function FadvCreditCardValidator(options) {
    return function (control) {
        /** This replicates the built in validation, but this is the only way to get that information to the form group */
        const results = [];
        const card = control.value;
        if (!card)
            return { required: true };
        Object.keys(defaultCreditCard).forEach((k) => {
            const key = k;
            if (!card[key]) {
                results.push({ [key]: true });
            }
        });
        if (card.month && card.year) {
            const month = parseInt(card.month, 10);
            const year = parseInt(card.year, 10);
            const current = new Date(Date.now());
            const currentMonth = current.getMonth();
            const currentYear = current.getFullYear();
            // Month is indexed at 0, but this code is intentional because credit cards expire at the end of the month
            // so if it's march, and the card expires in march, current.getMonth will return 2 and it's a valid expiration date.
            if (isNaN(month) || isNaN(year) || (year === currentYear && month <= currentMonth)) {
                results.push({ expiration: true });
            }
        }
        if (card.cardNumber && !(options === null || options === void 0 ? void 0 : options.disableLuhn) && !checkLuhn(card.cardNumber)) {
            results.push({ cardNumberLuhn: true });
        }
        return results.length ? results.reduce((acc, val) => (Object.assign(Object.assign({}, acc), val)), {}) : null;
    };
}
const defaultCreditCard = {
    firstName: '',
    lastName: '',
    cardNumber: '',
    month: '',
    year: '',
    csc: '',
};
class FadvCreditCardComponent extends mixinFormControl(FadvBaseComponent) {
    constructor(el) {
        super(el);
        this.changed = new EventEmitter();
        this.registerSubscription(this.afterViewInit, () => {
            this.registerFormValueChangeHandler((value) => this.handleCreditCardValueChange(value));
        });
        this.registerSubscription(this.onChanges, (changes) => {
            if ('value' in changes) {
                this.handleCreditCardValueChange(changes.value.currentValue);
            }
        });
    }
    get invalid() {
        if (!this.invalidInputs)
            return true;
        // if any input is invalid, this component is invalid.
        return Object.keys(this.invalidInputs).some((k) => {
            const key = k;
            return this.invalidInputs && this.invalidInputs[key];
        });
    }
    get valid() {
        return !this.invalid;
    }
    handleBlur() {
        // considered the credit card touched when any internal components are focused.
        this.onTouched();
    }
    handleCreditCardChange(event) {
        this.reportChange(event.detail);
        this.changed.emit(event.detail);
    }
    handleValidationResults(event) {
        this.invalidInputs = event.detail;
    }
    handleCreditCardValueChange(creditCard) {
        const value = creditCard || defaultCreditCard;
        Object.assign(this.elementRef.nativeElement, Object.assign(Object.assign({}, defaultCreditCard), value));
    }
}
FadvCreditCardComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvCreditCardComponent, deps: [{ token: ElementRef }], target: i0.ɵɵFactoryTarget.Component });
FadvCreditCardComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.3", type: FadvCreditCardComponent, selector: "fadv-credit-card", inputs: { value: "value", disabled: "disabled", firstName: "firstName", lastName: "lastName", cardNumber: "cardNumber", month: "month", year: "year", csc: "csc", firstNameLabel: "firstNameLabel", lastNameLabel: "lastNameLabel", cardNumberLabel: "cardNumberLabel", monthLabel: "monthLabel", yearLabel: "yearLabel", cscLabel: "cscLabel", cscTooltip: "cscTooltip", cscInfoBubbleLabel: "cscInfoBubbleLabel", floating: "floating", variant: "variant", pill: "pill", readonly: "readonly", validateLuhn: "validateLuhn" }, outputs: { valueChange: "valueChange", changed: "changed" }, host: { listeners: { "sl-blur": "handleBlur()", "credit-card-changed": "handleCreditCardChange($event)", "credit-card-validation-results": "handleValidationResults($event)" } }, providers: [createFormControlProvider(FadvCreditCardComponent)], usesInheritance: true, ngImport: i0, template: '', isInline: true });
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "firstName", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "lastName", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "cardNumber", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "month", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "year", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "csc", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "firstNameLabel", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "lastNameLabel", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "cardNumberLabel", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "monthLabel", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "yearLabel", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "cscLabel", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "cscTooltip", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "cscInfoBubbleLabel", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "floating", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "variant", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "pill", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "readonly", void 0);
__decorate([
    Forward()
], FadvCreditCardComponent.prototype, "validateLuhn", void 0);
__decorate([
    Track()
], FadvCreditCardComponent.prototype, "changed", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvCreditCardComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fadv-credit-card',
                    template: '',
                    providers: [createFormControlProvider(FadvCreditCardComponent)],
                    inputs: [formControlValueInput, formControlDisabledInput],
                    outputs: [formControlValueOutput],
                }]
        }], ctorParameters: function () {
        return [{ type: i0.ElementRef, decorators: [{
                        type: Inject,
                        args: [ElementRef]
                    }] }];
    }, propDecorators: { firstName: [{
                type: Input
            }], lastName: [{
                type: Input
            }], cardNumber: [{
                type: Input
            }], month: [{
                type: Input
            }], year: [{
                type: Input
            }], csc: [{
                type: Input
            }], firstNameLabel: [{
                type: Input
            }], lastNameLabel: [{
                type: Input
            }], cardNumberLabel: [{
                type: Input
            }], monthLabel: [{
                type: Input
            }], yearLabel: [{
                type: Input
            }], cscLabel: [{
                type: Input
            }], cscTooltip: [{
                type: Input
            }], cscInfoBubbleLabel: [{
                type: Input
            }], floating: [{
                type: Input
            }], variant: [{
                type: Input
            }], pill: [{
                type: Input
            }], readonly: [{
                type: Input
            }], validateLuhn: [{
                type: Input
            }], changed: [{
                type: Output
            }], handleBlur: [{
                type: HostListener,
                args: ['sl-blur']
            }], handleCreditCardChange: [{
                type: HostListener,
                args: ['credit-card-changed', ['$event']]
            }], handleValidationResults: [{
                type: HostListener,
                args: ['credit-card-validation-results', ['$event']]
            }] } });

class FadvCreditCardModule {
}
FadvCreditCardModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvCreditCardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FadvCreditCardModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.3", ngImport: i0, type: FadvCreditCardModule, declarations: [FadvCreditCardComponent], imports: [FadvCommonModule], exports: [FadvCreditCardComponent] });
FadvCreditCardModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvCreditCardModule, imports: [FadvCommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvCreditCardModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [FadvCreditCardComponent],
                    imports: [FadvCommonModule],
                    exports: [FadvCreditCardComponent],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { FadvCreditCardComponent, FadvCreditCardModule, FadvCreditCardValidator };
